import React, { useState } from 'react'
import styles from './Labels.module.css'
import { COLOURS, plural, type ContentField, type IdMode, type LabelFormat, type LabelJob, type LabelPerson, type Purpose } from './model'

// The draft preview (§7m.4, h 4691-4712). No scannable value and no printable
// file exists before Generate commits the identities, so the preview draws a
// deliberately non-decodable block where the QR goes, a sample Display ID, and
// a diagonal DRAFT watermark over every label and sheet; it never encodes
// anything. The text is the batch's own (the first selected person, their
// group, the job and its variety, as switched on), so what is too long shows.
// In print media the whole preview is replaced by a line saying it cannot be
// printed. Text is clipped here with an ellipsis, not fitted: the fitter that
// wraps and shrinks (§7m.7) must share one measurement rule with the PDF
// renderer, and both arrive with P7-3.

// Sample identities, never allocated: V4's samples per kind of label.
const SAMPLE_ID: Record<Purpose | IdMode, string> = { worker: '0142', Numeric: '1042', Alphanumeric: '04KM', item: '' }

export interface DraftPreviewProps {
  purpose: Purpose
  format: LabelFormat | undefined
  colour: string
  canColour: boolean
  idMode: IdMode
  // Assigned item labels and Worker ID labels name a person; unassigned ones do not.
  named: boolean
  people: LabelPerson[]
  job: LabelJob | undefined
  printFields: ContentField[]
  total: number
}

export function DraftPreview(p: DraftPreviewProps) {
  const [view, setView] = useState<'single' | 'sheet'>('single')
  const roll = p.format?.medium === 'Roll'
  return (
    <section className={styles.draftPreview} aria-label="Draft preview">
      <div className={styles.draftHead}>
        <span className={styles.draftTitle}>Draft preview</span>
        <div className={styles.pillSeg} role="radiogroup" aria-label="Preview">
          {(['single', 'sheet'] as const).map((v) => (
            <button
              key={v}
              type="button"
              role="radio"
              aria-checked={view === v}
              className={[styles.pillSegBtn, view === v ? styles.pillSegOn : ''].join(' ')}
              onClick={() => setView(v)}
            >
              {v === 'single' ? 'Single label' : roll ? 'Full roll' : 'Full sheet'}
            </button>
          ))}
        </div>
      </div>
      <div className={styles.draftScreen}>
        {!p.format ? (
          <div className={styles.draftEmpty}>Choose a format to see the preview.</div>
        ) : (
          <div className={styles.draftStage}>
            <div className={styles.draftWatermark} aria-hidden="true">
              <span>DRAFT</span>
            </div>
            {view === 'single' ? <DraftLabel {...p} /> : <DraftSheet {...p} format={p.format} />}
            <span className={styles.draftCaption}>Placeholder code · not scannable</span>
          </div>
        )}
        <p className={styles.draftNote}>
          The preview uses your real names, job and format, but carries no identity and cannot be printed. Real codes exist only after Generate commits
          them. Long text is cut off here, not fitted: fitting arrives with the PDF renderer.
        </p>
      </div>
      <p className={styles.draftPrint}>Preview cannot be printed. Generate and commit the batch first.</p>
    </section>
  )
}

// One label: the placeholder block, the sample ID in the batch colour, and the
// text lines the batch would print.
function DraftLabel(p: DraftPreviewProps & { mini?: boolean }) {
  const colour = COLOURS.find((c) => c.name === (p.canColour ? p.colour : 'Black')) || COLOURS[0]
  const person = p.people[0]
  const isWorker = p.purpose === 'worker'
  const lines: { key: string; text: string; muted?: boolean }[] = []
  if (p.named) lines.push(person ? { key: 'name', text: person.name || 'Unnamed person' } : { key: 'name', text: 'Nobody selected yet', muted: true })
  if (p.printFields.includes('group') && p.named && person?.group_name) lines.push({ key: 'group', text: person.group_name })
  if (!isWorker && p.job) {
    const jobText = [p.printFields.includes('job') ? p.job.name : null, p.printFields.includes('variety') ? p.job.variety : null].filter(Boolean).join(' · ')
    if (jobText) lines.push({ key: 'job', text: jobText })
  }
  if (p.mini) {
    return (
      <div className={styles.draftMini} aria-hidden="true">
        <span className={styles.draftBlockMini} />
      </div>
    )
  }
  return (
    <div className={styles.draftLabel} data-testid="draft-label">
      <div className={styles.draftCode}>
        <div className={styles.draftBlock} role="img" aria-label="Placeholder code, not scannable">
          <span className={[styles.draftCorner, styles.draftTl].join(' ')} />
          <span className={[styles.draftCorner, styles.draftTr].join(' ')} />
          <span className={[styles.draftCorner, styles.draftBl].join(' ')} />
        </div>
      </div>
      <div className={styles.draftText}>
        <span className={styles.draftId} style={{ background: colour.bg, color: colour.fg }} title="A sample, not an allocated Display ID">
          {SAMPLE_ID[isWorker ? 'worker' : p.idMode]}
        </span>
        {lines.map((l, i) => (
          <span key={l.key} className={[i === 0 ? styles.draftLineMain : styles.draftLine, l.muted ? styles.draftMuted : ''].join(' ')} title={l.text}>
            {l.text}
          </span>
        ))}
      </div>
    </div>
  )
}

// The sheet as it will be used: A4 always starts at position 1 of a fresh
// sheet and leaves the remainder blank (h 4708-4712). A roll is continuous.
function DraftSheet(p: DraftPreviewProps & { format: LabelFormat }) {
  const f = p.format
  if (f.medium === 'Roll') {
    const shown = Math.min(Math.max(p.total, 1), 4)
    return (
      <>
        <div className={styles.draftRoll}>
          {Array.from({ length: shown }, (_, i) => (
            <DraftLabel key={i} {...p} />
          ))}
        </div>
        <div className={styles.draftSheetNote}>
          Continuous roll. {plural(shown, 'label')} shown{p.total > shown ? ` of ${p.total.toLocaleString('en-NZ')}` : ''}.
        </div>
      </>
    )
  }
  const per = Math.max(1, f.labels_per_sheet || f.rows * f.columns || 1)
  const cols = Math.max(1, f.columns || 1)
  const used = Math.min(p.total, per)
  const sheets = Math.ceil(p.total / per)
  return (
    <>
      <div className={styles.draftSheet} style={{ gridTemplateColumns: `repeat(${cols}, 1fr)` }} role="img" aria-label={`Sheet 1: ${used} of ${per} positions used`}>
        {Array.from({ length: per }, (_, i) => (
          <div key={i} className={[styles.draftCell, i < used ? styles.draftCellUsed : ''].join(' ')}>
            {i < used && <DraftLabel {...p} mini />}
          </div>
        ))}
      </div>
      <div className={styles.draftSheetNote}>
        Every batch starts at position 1 of a fresh sheet. {used} of {per} positions used on sheet 1
        {sheets > 1 ? `, across ${sheets.toLocaleString('en-NZ')} sheets in all` : ''}. The remainder is left blank on purpose.
      </div>
    </>
  )
}
